import once from 'lodash.once'
import {CorePlugin, Utils, Playback, type SupportedVersion} from '@clappr/core'
import {Events} from 'common/types/events'
import type {Options} from 'common/types/options'
import {Log} from 'utils/log'
import {isDebug, isDevelopment} from 'utils/env'
import type {TnsConfig} from 'common/api/records'

export class Tns<T extends Options> extends CorePlugin<T> {
  public get name(): string {
    return 'tns'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get params(): TnsConfig[] {
    return this.core.options.tns || ([] as TnsConfig[])
  }

  private get canPlay(): boolean {
    return this.core.activePlayback.getPlaybackType() !== Playback.NO_OP
  }

  public bindEvents(): void {
    if (!this.core.isReady) {
      this.onPlay = once(this.onPlay)
      this.onEnded = once(this.onEnded)
    }

    this.listenToOnce(this.core, Events.CORE_READY, this.onReady)
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenToOnce(activeContainer, Events.CONTAINER_PLAY, this.onPlay)
      this.listenToOnce(activeContainer, Events.CONTAINER_ENDED, this.onEnded)
    }
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  private onReady(): void {
    if (this.canPlay) {
      this.params.forEach(({player}) => {
        if (player) {
          this.trackPixel(player.replace('random_number', Utils.uniqueId('')))
        }
      })
    }
  }

  private onPlay(): void {
    this.params.forEach(({playStart}) => {
      if (playStart) {
        this.trackPixel(playStart.replace('random_number', Utils.uniqueId('')))
      }
    })
  }

  private onEnded(): void {
    this.params.forEach(({playEnd}) => {
      if (playEnd) {
        this.trackPixel(playEnd.replace('random_number', Utils.uniqueId('')))
      }
    })
  }

  private trackPixel(url: string): void {
    if (isDebug()) {
      Log.debug('tns', url)
    }

    if (isDevelopment()) {
      return
    }

    const image = new Image()

    image.src = url
  }
}
